import React from 'react';

import { Strong } from '@constellation/core';
import { AppContent } from 'content/AppContent';

import { MetaContentValues } from '../MetaContentValues';

const termsAndConditions: AppContent['termsAndConditions'] = {
  metaContent: MetaContentValues.TermsAndConditions,
  pageTitle: 'BM Solutions Intermediaries Online Website Conditions',

  infoPanel: {
    contentSections: [
      {
        sectionID: 'general-section',
        isAccordion: false,
        sectionBody: [
          {
            text: 'Date published: May 2025',
          },
          {
            subheading: '1. General',
          },
          {
            text: 'Which Terms Apply?',
          },
          {
            text: (
              <>
                <Strong>Directly Authorised Firms: </Strong>If you are acting
                for a directly authorised intermediary firm, we and your firm
                will have previously entered into an Intermediary Agreement
                governing the submission by you and your colleagues of Mortgage
                Applications to us. That Intermediary Agreement states in
                particular that: (i) the use of our mortgage intermediary
                website and all the facilities provided in connection with it
                (referred to hereafter as "this website"; by you and your
                colleagues in relation to the submission of applications is
                subject to these On-line Terms of Use (referred to hereafter as
                "these conditions”. You should therefore read these conditions
                together with the Intermediary Agreement (as may be varied by
                the parties from time to time). In the event of any conflict
                between these conditions and the Intermediary Agreement, the
                relevant term(s) of the Intermediary Agreement will take
                precedence.
              </>
            ),
          },
          {
            text: (
              <>
                <Strong>Appointed Representative of a Principal Firm: </Strong>
                If you are acting for an Appointed Representative of a Principal
                Firm, we and your Principal Firm will have previously entered
                into an Intermediary Agreement governing the submission by you
                and your colleagues of Mortgage Applications to us. You should
                ensure that your Principal Firm has provided to you the
                provisions of that Intermediary Agreement that apply to its
                Appointed Representatives. That Intermediary Agreement states in
                particular that: (i) the use of our mortgage intermediary
                website and all the facilities provided in connection with it
                (referred to hereafter as "this website"; by you and your
                colleagues in relation to the submission of Mortgage
                Applications is subject to these On-line Terms of Use (referred
                to hereafter as "these conditions"). You should therefore read
                these conditions together with the relevant provisions of the
                Intermediary Agreement we have in place with your Principal Firm
                (as may be varied by the parties from time to time). In the
                event of any conflict between these conditions and the
                Intermediary Agreement, the relevant term(s) of the Intermediary
                Agreement will take precedence.
              </>
            ),
          },
          {
            text: '1.1 It is important that you read and understand and accept these conditions before you start to use this website. By confirming your acceptance, you are accepting these conditions in their entirety. We recommend that you print a copy of these conditions for your reference.',
          },
          {
            text: '1.2 We may amend these conditions at any time without notice by publishing a new version of these conditions on this website. Your use of this website after any amendment will be subject to these conditions as amended.',
          },
        ],
      },
      {
        sectionHeading: '2. Scope',
        headingLevel: 'h2',
        sectionID: 'scope',
        sectionBody: [
          {
            text: '2.1 These conditions set out the terms upon which you may use this website, and upon which we will deal with you if and when you introduce an Applicant to us through this website. They do not apply to applications you introduce to any other part of Lloyds Banking Group (where other terms may apply).',
          },
          {
            text: '2.2 We reserve the right not to accept any Mortgage Application submitted by you for any reason including where it is outside our lending Policies, does not comply with Applicable Standards, is submitted in breach of our Intermediary Agreement, and, to the extent permitted by law and regulation, without giving any explanation.',
          },
          {
            text: '2.3 You are responsible for complying with, and providing all notices to Applicants that are required by, Data Protection Laws with regard to the processing of their Personal Data (including electronically). If consent is required in accordance with Data Protection Laws, you must obtain consent from the Applicant prior to the transfer of their Personal Data to us in connection with any Mortgage Application or otherwise, or ensure that an alternative appropriate lawful basis is in place to transfer the applicant’s personal data to us.',
          },
          {
            text: '2.4 The products and services advertised via this website are subject to availability and may be withdrawn or amended without notice. It is your responsibility to check our lending Policies and processes and comply with them and any of our Policies notified to you including those relating to vulnerable customers. We can amend any of the contents of the website at any time and without notice.',
          },
          {
            text: '2.5 Information on this website about products is provided for the benefit of residents of and properties located in the United Kingdom only. You must have your registered offices within the UK and be ordinarily resident within the UK. Under no circumstances will we accept any Mortgage Applications where any of these criteria are not met.',
          },
        ],
      },
      {
        sectionHeading: '3. Definitions',
        headingLevel: 'h2',
        sectionID: 'definitions',
        sectionBody: [
          {
            text: 'In these conditions, the following definitions apply:',
          },
          {
            text: '"Administrator" means a nominated person representing an Organisation who may submit and monitor the progress of applications on a read-only or full access basis on behalf of any Consultant(s) employed or otherwise engaged by that Organisation. For the avoidance of doubt a Consultant, Supervisor or Supervisor Consultant may also act as Administrator. An Administrator must have a unique email address and User ID for each Organisation.',
          },
          {
            text: '“Applicable Standards”: means any applicable law; regulation; direction; court order; judgment; decree; policy, rule;  guidance; principle; supervisory statement; thematic review or order issued by a regulatory body including the FCA, ICO PRA or issued by any other government department or agency and any industry code, guidelines, policy or standard required by a trade body which either of the Parties would be expected to comply. For the avoidance of doubt, reference to Applicable Standards in these conditions includes the Consumer Duty and separate reference to the Consumer Duty in these conditions does not remove it from this definition of Applicable Standards;',
          },
          {
            text: `3.1 "Applicant" means a person or persons applying for a mortgage product offered by us;`,
          },
          {
            text: `"Appointed Representative" means a person who acts as a Principal Firm's representative in accordance with Section 39 of the Financial Services and Markets Act 2000;`,
          },
          {
            text: '"Consultant" means a nominated person representing an Organisation who will submit Mortgage Applications on behalf of that Organisation;',
          },
          {
            text: '“Data Protection Laws” means the Data Protection Act 2018, the Privacy and Electronic Communication (EC Directive) Regulations 2003 as amended and all other applicable laws, regulations or codes of practice relating to data protection or the privacy of individuals;',
          },
          {
            text: '“Intermediary Agreement” means the agreement we have entered into with your firm or in the case of Appointed Representative your Principal Firm governing the submission of Mortgage Applications to us and your responsibilities in relation to this;',
          },
          {
            text: '"Lloyds Banking Group" means the Lloyds Banking Group plc (registered number SC095000) whose registered office is at The Mound Edinburgh EH1 1YZ and any holding companies or subsidiary undertakings (as such terms are defined in the Companies Act 2006) of it or of any such holding company, in each case from time to time and a Lloyds Banking Group Company will mean any such company;',
          },
          {
            text: '“Mortgage Application" means any application for a mortgage, product transfer or further advance from us that you send to us on behalf of an Applicant;',
          },
          {
            text: '"Organisation" means the sole principal, partnership, company or limited partnership by which the relevant user is employed or otherwise engaged (other than the Principal Firm for whom an Organisation is acting as an Appointed Representative);',
          },
          {
            text: 'Policies:  means such internal policies, processes, criteria and codes of ours as may be notified to you and/or as updated from time to time;',
          },
          {
            text: '"Principal Firm" means an intermediary firm which engages with, controls, co-ordinates and operates with product providers through, a network of Appointed Representatives;',
          },
          {
            text: `"Supervisor" means a nominated person representing an Organisation that will authorise access for that Organisation's Consultants and Administrators and none other. They will NOT be authorised to submit Mortgage Applications on behalf of that Organisation;`,
          },
          {
            text: `"Supervisor - Consultant" means a nominated person representing an Organisation that will authorise access for that Organisation's Consultants and Administrators and none other. They will also be authorised to submit Mortgage Applications on behalf of the Organisation;`,
          },
          {
            text: '"User" means any user of this website;',
          },
          {
            text: '"you" and "your" means (as appropriate) the Organisation who introduces an applicant to us or any individual applying to register to use this website on behalf of that Organisation.',
          },
          {
            text: '"we", "us" and "our" means Bank of Scotland plc T/A Birmingham Midshires, Registered in Scotland No. SC327000. Registered Office: The Mound, Edinburgh EH1 1YZ.',
          },
        ],
      },
      {
        sectionHeading: '4. User Role and Registration',
        headingLevel: 'h2',
        sectionID: 'user-role-and-registration',
        sectionBody: [
          {
            text: '4.1 As part of the registration process you must accept these conditions and obtain from us (via this website) a User ID (Identification) and password. Your User role will be automatically assigned or you may be required to select a role as defined in paragraph 3 above. We will only issue you with a User ID and password if you satisfy certain conditions, as set out from time to time on this website.',
          },
          {
            text: '4.2 We can refuse to issue to you a User ID or password at any time, and without offering any explanation. If you have been issued with a User ID and password, we can revoke your use of such User ID or password at any time, and without offering any explanation.',
          },
          {
            text: 'Each individual User must have a unique registration and User ID and password approved by us which may not be shared and every firm must have at least one Supervisor or Supervisor Consultant registered with us at all times.',
          },
          {
            text: 'The role responsibilities are:',
          },
          {
            subheading: {
              headingLevel: 'h5',
              size: 's2',
              displayText: '4.3 Consultant',
            },
          },
          {
            text: 'By registering as Consultant for your Organisation you are accepting responsibility for:',
          },
          {
            text: '4.3.1 ensuring that you register for your Organisation:',
          },
          {
            text: '4.3.2 submitting Mortgage Applications on behalf of your Organisation and monitoring the progress of each Mortgage Application including contacting us should any information on the Mortgage Application be incorrect, need updating or if there is anything which you consider we need to be aware of about the Mortgage Application which has not yet been communicated to us, and providing us with any additional information we may require from time to time about the Applicant or the Mortgage Application',
          },
          {
            text: '4.3.3 ensuring that your e-mail address given to us is correct;',
          },
          {
            text: '4.3.4 ensuring that your personal details given to us are up to date and correct and keeping these up to date at all times;',
          },
          {
            text: "4.3.5 authorising a suitable person to become your Organisation's Administrator for you and on your behalf, to monitor progress of Mortgage Applications submitted by you.",
          },
          {
            text: '4.3.6 ensuring that you do not share your email address or User ID or password with anyone else.',
          },
          {
            subheading: {
              headingLevel: 'h5',
              size: 's2',
              displayText: '4.4 Supervisor',
            },
          },
          {
            text: 'By registering as Supervisor for your Organisation you are accepting responsibility for administering the following User rights for your Organisation:',
          },
          {
            text: '4.4.1 authorising other Users for your Organisation to use this website at the correct role level;',
          },
          {
            text: '4.4.2 ensuring that your e-mail address and those of Users registered for your Organisation given to us are correct and kept up to date at all times;',
          },
          {
            text: '4.4.3 ensuring that neither you or any Users registered for your Organisation share an email address or User ID or password with anyone else;',
          },
          {
            text: '4.4.4 ensuring that only Users, who we have not given you notice of as being persons we regard as unsuitable and who your Organisation deems suitable to submit Mortgage Applications, are registered as "Consultants". As a Supervisor you will not be authorised to submit Mortgage Applications personally;',
          },
          {
            text: "4.4.5 authorising your Organisation's Users to change roles where you deem appropriate;",
          },
          {
            text: '4.4.6 Immediately deleting Users who leave your Organisation or who will become temporarily inactive for a period of longer than 60 days for any reason;',
          },
          {
            text: '4.4.7 ensuring that there are a sufficient number of Supervisors for your Organisation to maintain all Users registered, with at least one Supervisor or Supervisor - Consultant registered for your Organisation at all times;',
          },
          {
            text: '4.4.8 ensuring that your personal details given to us are up to date and correct;',
          },
          {
            text: '4.4.9 at least quarterly checking all information provided by you or other Users in your Organisation including access permissions and roles are accurate, necessary and up to date and providing evidence of these controls to us on request;',
          },
          {
            text: '4.4.10 ensuring that any and all Organisation personnel registered as a User are aware of and adhere to their responsibilities as outlined in the relevant part of this paragraph 4.',
          },
          {
            subheading: {
              headingLevel: 'h5',
              size: 's2',
              displayText: 'Supervisor - Consultant',
            },
          },
          {
            text: 'By registering as Supervisor - Consultant for your Organisation you must comply with:',
          },
          {
            text: '4.5.1 all the responsibilities of the Supervisor listed in paragraph 4.4; and',
          },
          {
            text: '4.5.2 all the responsibilities of the Consultant listed in paragraph 4.3.',
          },
          {
            subheading: {
              headingLevel: 'h5',
              size: 's2',
              displayText: '4.6 Administrator',
            },
          },
          {
            text: 'By registering as Administrator for your Organisation you must comply with:',
          },
          {
            text: '4.6.1 ensuring that you register for your Organisation and none other;',
          },
          {
            text: '4.6.2 ensuring that your e-mail address given to us is correct;',
          },
          {
            text: '4.6.3 ensuring that you do not share your email address or User ID or password with anyone else; and',
          },
          {
            text: '4.6.4 ensuring that your personal details given to us are up to date and correct and keeping those up to date at all times.',
          },
        ],
      },
      {
        sectionHeading: '5. Termination',
        headingLevel: 'h2',
        sectionID: 'termination',
        sectionBody: [
          {
            text: '5.1 We can terminate your access to the system/website at any time.',
          },
          {
            text: '5.2 We can restrict or refuse access to all or any part of this website at any time, and without notice or explanation.',
          },
          {
            text: '5.3 Any termination under this paragraph 5 will not affect any accrued rights or liabilities of either party under these conditions nor will it affect the coming into force of any provision of these terms which is expressly or by implication intended to come into force on or after such termination.',
          },
        ],
      },
      {
        sectionHeading: '6. Security',
        headingLevel: 'h2',
        sectionID: 'security',
        sectionBody: [
          {
            text: '6.1 Certain areas of this website are secure. You may only access these secure areas once you have registered with us and you must comply with our Policies at all times; Once you have been issued with a password, you must not disclose it to anyone. Responsibility for the security of your password rests with you. If your password is revealed to a third party (including another individual within your Organisation who was not the person to whom we supplied), whether deliberately or in error or otherwise, we will not be liable to you or to any applicant for any loss incurred and you agree to indemnify us in respect of losses or expenses incurred by us as a result of the unauthorised use of your password by any third party. If you forget your password, or if you believe that someone else knows your password, you must let us know immediately.',
          },
          {
            text: '6.2 You can tell which areas of this website are secure by the appearance of a security symbol or padlock in the browser status bar. Any information sent to us using a secure area will be encrypted to a high standard and sent on to us in a secure format.',
          },
          {
            text: '6.3 There may be other areas of this website which are not secure. We recommend that you do not send to us sensitive personal and financial information relating to you or any Applicant except via clearly marked secure e-mail and form features.',
          },
          {
            text: 'Calls to any of the telephone numbers on this website may be monitored and recorded, to help us to track usage and maintain the quality of service we provide to you. Further Information on Security is available on this Website.',
          },
        ],
      },
      {
        sectionHeading: '7. Website',
        headingLevel: 'h2',
        sectionID: 'website',
        sectionBody: [
          {
            text: '7.1 You may make fair and reasonable use of our website for the purpose of introducing Applicants to us. We aim to make:',
          },
          {
            text: '1) the unsecure areas of this website constantly available for your use; and',
          },
          {
            text: '2) the secure areas of this website available for your use between 8.00am and 8.00pm Monday to Friday, 8.00am until 6.00pm Saturday.',
          },
          {
            text: 'We cannot however guarantee this availability and will not be responsible for any losses suffered if you cannot access the relevant part of this website at the relevant time (for example if it results in you not being able to submit a Mortgage Application for a particular product). We are also not responsible if the means of communication you use to access this website to send us information, or to receive information from us, does not work properly.',
          },
          {
            text: '7.2 Access to this website may be suspended temporarily and without notice in the case of system failure, maintenance or repair or any other for reason beyond our reasonable control including an increase in the volume of usage of the website.',
          },
        ],
      },
      {
        sectionHeading: '8. Restrictions',
        headingLevel: 'h2',
        sectionID: 'restrictions',
        sectionBody: [
          {
            text: 'Except as expressly set out in these terms, you undertake not to;',
          },
          {
            text: '8.1 Use the website for any unlawful purposes, including fraud, terrorism or in connection with a criminal offence under the Applicable Standards regulations, against public order or applicable ethical standards and codes; and',
          },
          {
            text: '8.2 use the website in any way that could be harmful to our systems or data (including uploading any material that otherwise contains a virus, Trojan horse, worm or other malicious code, or using the website in such a way as to impair the operation of the website.',
          },
        ],
      },
      {
        sectionHeading: '9. Liability Arising from Use of the Website',
        headingLevel: 'h2',
        sectionID: 'liability-arising-from-use-of-the-website',
        sectionBody: [
          {
            text: '9.1 All content on this website is provided for your information and for illustrative purposes only. We aim to ensure that the content is current and accurate, but do not accept any liability to you, any Applicant or any other person where it is not, whether in contract, tort, negligence, or otherwise, and whether direct or indirect. If you want to rely on any information provided in this website, you should check its currency and accuracy directly with us, before placing any reliance on it.',
          },
          {
            text: '9.2 We have provided within the secure area of this website various tools for your use. These may change from time to time and we may withdraw tools and/or introduce new ones. While we have made every effort to ensure the accuracy and functional efficiency of these tools, we cannot guarantee this. We accept no liability to you, any Applicant or any other person as a result of any errors, technical defects, miscalculations, or lack of functionality within the tools.',
          },
          {
            text: '9.3 Where we provide on this website a direct link to any other site not under our control, we accept no liability for the content of, or your use of, that other site. If you use any such link, you leave this website. We neither guarantee the accuracy of the content of any linked site, nor do we necessarily endorse or approve of that content. We cannot guarantee that you will be able to access the site via the link on this website at any time.',
          },
          {
            text: '9.4 We aim to ensure that this website is free of all viruses and technical defects of any description. Due to the nature of the internet however, and the possibility of third party interference, this cannot be guaranteed, and we will not be liable for any damage or loss caused as a result of your use of this website.',
          },
          {
            text: '9.5 The content of this website does not constitute an offer to contract. The information does not constitute advice or a recommendation to you or to any Applicant. You are responsible for providing proper advice to Applicants on the suitability of our products. We do not guarantee that the products and services advertised on this website will be suitable for any Applicant.',
          },
        ],
      },
      {
        sectionHeading: '10. Website Intellectual Property',
        headingLevel: 'h2',
        sectionID: 'website-intellectual-property',
        sectionBody: [
          {
            text: '10.1 Unless otherwise stated, the copyright, trademarks and other intellectual property rights in all materials on this website (including without limitation text, tools, photographs and graphical images) are owned by us or our licensors.',
          },
          {
            text: '10.2 You are allowed (as reasonably appropriate) to use, download and print the information and tools available on this website for the purpose only of introducing Applicants and submitting Mortgage Applications to us and carrying out any obligations under your Intermediary Agreement with us. You may not otherwise extract or distribute the material contained within this website, or use the material and tools, as well as the Birmingham Midshires brand for any commercial benefit to yourself or others, including representing yourself as Birmingham Midshires. If you wish to use this website content for any other purpose, or in any other way (such as, without limitation, to provide a link to this website from another site), you must contact us to obtain our prior written consent. We may not give consent. If any consent is given we will explain as part of the consent how you will be permitted to use this website, and its contents. You agree that we may use any data, information, content or material that is made available to us through your use of this website for our own business purposes.',
          },
          {
            text: '10.3 Any rights not expressly granted in these conditions are reserved.',
          },
        ],
      },
      {
        sectionHeading: '11. Miscellaneous',
        headingLevel: 'h2',
        sectionID: 'miscellaneous',
        sectionBody: [
          {
            text: '11.1 These conditions and your use of this website are governed by, and will be construed in accordance with, English law. The courts of England will have exclusive jurisdiction to determine any disputes arising.',
          },
          {
            text: '11.2 This website is not intended for your use if you, or the Applicant, are resident or the property to which the Mortgage Application relates, is located outside of the United Kingdom; we therefore do not guarantee that this website complies with the laws and regulations of any other jurisdiction.',
          },
          {
            text: '11.3 No forbearance or delay by either party in enforcing these conditions will prejudice or restrict the rights of that party, nor will any waiver of its rights operate as a waiver of any subsequent breach. No right, power or remedy conferred on or reserved for either party is exclusive of any other right, power or remedy available to that party and each such right, power or remedy will be cumulative.',
          },
          {
            text: '11.4 The illegality, invalidity or unenforceability of any of these conditions (or any part of a condition) will not affect the legality, validity or enforceability of any of the other conditions (or the remaining parts of a condition).',
          },
          {
            text: '11.5 You may not assign, subcontract or otherwise transfer any rights and obligations under these conditions whether in whole or in part without our prior written consent. We may assign, subcontract or otherwise transfer our rights and obligations without requiring your consent and any references to "we" and "us" include any assignee/transferee or subcontractor as applicable.',
          },
          {
            text: '11.6 Headings in these conditions are for convenience only and will not affect their interpretation.',
          },
        ],
      },
    ],
  },
};

export default termsAndConditions;
